import {ApiClient} from '@hconnect/apiclient'
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions
} from '@tanstack/react-query'
import {AxiosError} from 'axios'

import {Comment, CommentDto} from '../../events/types'
import {mapComment} from '../mappers'
import {CreateCommentParameter} from '../types'

export const ShiftCommentsQueryKey = 'shift-summary-comments'

type UseFetchShiftCommentsParams<TSelect = Comment[]> = {
  plantId: string
  from?: string
  to?: string
  timezone: string
  options?: Omit<UseQueryOptions<Comment[], AxiosError, TSelect>, 'queryKey' | 'queryFn'>
}

export const useFetchShiftComments = <TSelect = Comment[]>({
  plantId,
  from,
  to,
  timezone,
  options
}: UseFetchShiftCommentsParams<TSelect>) => {
  const path = `/shifthandover/${plantId}/shiftcomments`

  return useQuery<Comment[], AxiosError, TSelect>({
    queryKey: [ShiftCommentsQueryKey, from, to, plantId, timezone],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.get<CommentDto[]>(path, {
        params: {
          startDate: from,
          endDate: to
        },
        signal
      })
      return (response.data || []).map((comment) => mapComment(comment, timezone))
    },
    ...options
  })
}

type UseCreateShiftCommentParams = {
  plantId: string
  timezone: string
  options?: UseMutationOptions<Comment, AxiosError, CreateCommentParameter>
}

export const useCreateShiftComment = ({
  plantId,
  timezone,
  options
}: UseCreateShiftCommentParams) => {
  const queryClient = useQueryClient()

  return useMutation<Comment, AxiosError, CreateCommentParameter>({
    mutationFn: async ({submittedForTimestamp, description}) => {
      const path = `/shifthandover/${plantId}/shiftcomments`
      const payload = {
        submittedFor: submittedForTimestamp,
        description
      }

      const mutation = await ApiClient.axiosInstance.post<CommentDto>(path, payload)
      return mapComment(mutation.data, timezone)
    },
    ...options,
    onSuccess: async (data, variables, context, mutation) => {
      await queryClient.invalidateQueries({queryKey: [ShiftCommentsQueryKey]})
      await options?.onSuccess?.(data, variables, context, mutation)
    }
  })
}
